import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import {
  ChalkCard, ChalkIllustrationLines, DarkHeader,
  EmptyState, SectionTitle, UpgradeGateCard,
} from "../components/chalk/primitives";
import EraseRedrawSheet from "../components/chalk/EraseRedrawSheet";
import DrawItUpSheet from "../components/chalk/DrawItUpSheet";
import { startCheckout } from "../lib/checkout";
import { UPGRADE_GATES } from "../lib/upgradeGates";
import { eventSortKey } from "../lib/eventTime";

import { TrialBanner, ExpiredBanner } from "../components/chalk/chalkboard/BillingBanners";
import { NextPlayCard, humanWhen } from "../components/chalk/chalkboard/NextPlayCard";
import { QuickDrawGrid } from "../components/chalk/chalkboard/QuickDrawGrid";
import { DrawItUpFab } from "../components/chalk/chalkboard/DrawItUpFab";
import { useChalkboardSubscription } from "../components/chalk/chalkboard/useChalkboardSubscription";
import TeamLockBanner from "../components/chalk/TeamLockBanner";

export default function Chalkboard() {
  const { user, signout } = useAuth();
  const { activeTeam, teams, setActive, tier } = useTeam();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [athletes, setAthletes] = useState(0);
  const [cancelling] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [drawOpen, setDrawOpen] = useState(false);
  const [renewing, setRenewing] = useState(false);

  const { trial, expired } = useChalkboardSubscription();

  async function onRenew() {
    setRenewing(true);
    try {
      await startCheckout({ tier: "chalkboard", interval: "monthly", teamId: activeTeam?.id });
      // On success, browser is redirected — control won't return here.
    } catch (e) {
      toast.error(e.message || "Couldn't open checkout. Try again.");
      setRenewing(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!activeTeam) return;
      try {
        const [ev, msgs, ros] = await Promise.all([
          api.listEvents(activeTeam.id),
          api.listMessages(activeTeam.id),
          api.listRoster(activeTeam.id),
        ]);
        if (cancelled) return;
        const upcoming = (ev.events || []).filter(
          (e) => e.status !== "cancelled" &&
                 eventSortKey(e) >= Date.now() - 3600 * 1000
        );
        setEvents(upcoming.sort((a, b) => eventSortKey(a) - eventSortKey(b)));
        setMessages(msgs.messages || []);
        setAthletes((ros.athletes || []).length);
      } catch {
        // ignore — keep last state
      }
    }
    load();
    return () => { cancelled = true; };
  }, [activeTeam]);

  const isChalkboard = tier.coach_tier === "chalkboard" || tier.coach_tier === "league_os";
  const nextPlay = events[0] || null;
  const latestMessage = messages[0] || null;

  function onErase() {
    if (!nextPlay) return;
    if (activeTeam && !activeTeam.is_owner && activeTeam.permissions && !activeTeam.permissions.EVENT_CANCEL) {
      toast("Ask your head coach to turn on “Cancel or delay events” for you.");
      return;
    }
    if (!isChalkboard) {
      // No toast / error — start the upgrade flow with full analytics context.
      startCheckout({
        tier: "chalkboard",
        teamId: activeTeam?.id,
        context: { feature_name: UPGRADE_GATES.erase_and_redraw.featureName, source_screen: "chalkboard_next_play", current_tier: tier.coach_tier },
      }).catch((e) => toast.error(e?.message || "Couldn't open checkout."));
      return;
    }
    setEraseOpen(true);
  }

  function onErased(eventId, meta) {
    toast.success(`Erased. ${meta?.familyCount ?? 0} famil${meta?.familyCount === 1 ? "y" : "ies"} notified.`);
    setEvents((ev) => ev.filter((e) => e.id !== eventId));
  }

  return (
    <>
      <DarkHeader
        eyebrow={`${activeTeam ? activeTeam.season_label : ""}`}
        title={user.role === "assistant" ? `Hi, ${user.first_name}.` : `Hi, Coach ${user.last_name}.`}
        subtitle={activeTeam ? `${activeTeam.name}` : "Add a team to get started."}
        right={
          teams.length > 1 ? (
            <select
              data-testid="team-switcher"
              value={activeTeam?.id || ""}
              onChange={(e) => setActive(e.target.value)}
              style={{
                background: "transparent", color: "var(--chalk-white)",
                border: "0.5px solid rgba(245,240,232,0.20)",
                borderRadius: 999, padding: "6px 10px", fontSize: 12,
              }}
            >
              {teams.map((t) => (
                <option key={t.id} value={t.id} style={{ color: "var(--chalk-board)" }}>{t.name}</option>
              ))}
            </select>
          ) : null
        }
      />

      <div style={{ padding: "0 18px 24px" }}>
        <TrialBanner trial={trial} />
        <ExpiredBanner expired={expired} renewing={renewing} onRenew={onRenew} />
        <TeamLockBanner team={activeTeam} sourceScreen="chalkboard" />

        {/* Brand-new-coach helper. With no team there's nothing to schedule,
            send Chalk Talk to, or roster — point them straight at Roster
            where team creation lives. */}
        {!activeTeam && user.role !== "guardian" && (
          <ChalkCard
            data-testid="no-team-cta"
            onClick={() => navigate("/app/roster")}
            style={{
              borderLeft: "2px solid var(--green-mid)",
              cursor: "pointer", marginBottom: 14,
            }}
          >
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--green-mid)", fontWeight: 700 }}>
              First step
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: "var(--chalk-board)", marginTop: 6 }}>
              Create your first team.
            </div>
            <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
              Tap to head to Roster and add your team — practices, Chalk Talk, and RSVPs unlock from there.
            </div>
          </ChalkCard>
        )}

        <SectionTitle label="Next Play" />
        <NextPlayCard
          nextPlay={nextPlay}
          activeTeam={activeTeam}
          athletes={athletes}
          isChalkboard={isChalkboard}
          cancelling={cancelling}
          user={user}
          onErase={onErase}
          onSendTalk={() => navigate("/app/talk")}
          onAddFirstPlay={() => navigate("/app/playbook")}
        />

        <SectionTitle label="Quick Draw" />
        <QuickDrawGrid
          isChalkboard={isChalkboard}
          onPlay={() => navigate("/app/playbook")}
          onTalk={() => navigate("/app/talk")}
          onRoster={() => navigate("/app/roster")}
          onAi={() => (isChalkboard ? setDrawOpen(true) : navigate("/app/more"))}
        />

        <SectionTitle label="Latest Chalk Talk" />
        {latestMessage ? (
          <ChalkCard data-testid="latest-chalk-talk" onClick={() => navigate("/app/talk")}>
            <div style={{ fontSize: 14, color: "var(--chalk-board)" }}>{latestMessage.body}</div>
            <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginTop: 6 }}>
              {humanWhen(latestMessage.sent_at)}
            </div>
          </ChalkCard>
        ) : (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="Your team is listening, Coach."
            body="Post your first Chalk Talk."
            ctaLabel="Post your first Chalk Talk"
            onCta={() => navigate("/app/talk")}
          />
        )}

        {!isChalkboard && (
          <>
            <SectionTitle label="Unlock The Chalkboard" />
            <UpgradeGateCard
              {...UPGRADE_GATES.erase_and_redraw}
              context={{ source_screen: "chalkboard_home", current_tier: tier.coach_tier, team_id: activeTeam?.id }}
              onUpgrade={async () => {
                try {
                  await startCheckout({
                    tier: "chalkboard",
                    teamId: activeTeam?.id,
                    context: { feature_name: UPGRADE_GATES.erase_and_redraw.featureName, source_screen: "chalkboard_home", current_tier: tier.coach_tier },
                  });
                } catch (e) { toast.error(e.message); }
              }}
            />
          </>
        )}

        <div style={{ marginTop: 24, fontSize: 12, color: "var(--chalk-muted)", textAlign: "center" }}>
          <button
            onClick={() => { signout(); navigate("/"); }}
            style={{ background: "transparent", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer" }}
            data-testid="signout-link"
          >
            Sign out
          </button>
        </div>
      </div>

      <EraseRedrawSheet
        isOpen={eraseOpen}
        event={nextPlay ? { ...nextPlay, team_id: activeTeam?.id } : null}
        sport={activeTeam?.sport}
        onDismiss={() => setEraseOpen(false)}
        onCancelled={onErased}
      />

      <DrawItUpSheet
        isOpen={drawOpen}
        teamId={activeTeam?.id}
        onDismiss={() => setDrawOpen(false)}
        onPosted={() => api.listMessages(activeTeam.id).then((d) => setMessages(d.messages || [])).catch(() => {})}
      />

      {isChalkboard && activeTeam && (
        <DrawItUpFab onClick={() => setDrawOpen(true)} />
      )}
    </>
  );
}
